import { NavLink } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { navigation, type NavItem } from '../app/navigation';
import { cn } from '../lib/cn';
import { hasPermission } from '../lib/permissions';
import { BrandMark } from './BrandMark';
import { useSession } from '../features/session/useSession';

export interface SidebarProps {
  collapsed: boolean;
  isMobile: boolean;
  mobileOpen: boolean;
  onToggleCollapsed: () => void;
  onNavigate: () => void;
}

export function Sidebar({
  collapsed,
  isMobile,
  mobileOpen,
  onToggleCollapsed,
  onNavigate,
}: SidebarProps) {
  const { me } = useSession();
  const permissions = me?.permissions;
  const visibleNavigation = navigation
    .map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        hasPermission(permissions, item.permission),
      ),
    }))
    .filter((section) => section.items.length > 0);
  const showLabels = isMobile ? true : !collapsed;
  const scopeName = me?.currentScope?.name?.trim() || 'Ferry';
  const wordmark = `${scopeName} Tools`;

  return (
    <>
      {isMobile && mobileOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-gray-900/60 backdrop-blur-sm md:hidden"
          onClick={onNavigate}
        />
      ) : null}

      <aside
        aria-label="Primary"
        className={cn(
          'fixed top-0 bottom-0 left-0 z-40 flex flex-col border-r border-gray-200 bg-white transition-[transform,width] duration-200 ease-out',
          'dark:border-gray-800 dark:bg-gray-950',
          !isMobile && (collapsed ? 'w-20' : 'w-64'),
          isMobile && 'w-72',
          isMobile && (mobileOpen ? 'translate-x-0' : '-translate-x-full'),
        )}
      >
        <div
          className={cn(
            'flex h-16 items-center border-b border-gray-200 dark:border-gray-800',
            !isMobile && collapsed ? 'justify-center gap-1 px-1' : 'gap-3 px-4',
          )}
        >
          <BrandMark showWordmark={showLabels} wordmark={wordmark} />
          {!isMobile ? (
            <button
              type="button"
              onClick={onToggleCollapsed}
              className={cn(
                'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-100 hover:text-gray-900',
                'dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white',
                !collapsed && 'ml-auto',
              )}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <ChevronLeft
                className={cn(
                  'h-4 w-4 transition-transform',
                  collapsed && 'rotate-180',
                )}
                aria-hidden="true"
              />
            </button>
          ) : null}
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {visibleNavigation.map((section) => (
            <div key={section.title} className="mb-6 last:mb-0">
              {showLabels ? (
                <div className="px-2 pb-2 text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500">
                  {section.title}
                </div>
              ) : (
                <div
                  aria-hidden="true"
                  className="mx-2 mb-2 border-t border-gray-200 dark:border-gray-800"
                />
              )}
              <ul className="space-y-1">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <SidebarLink
                      item={item}
                      showLabel={showLabels}
                      onNavigate={onNavigate}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}

interface SidebarLinkProps {
  item: NavItem;
  showLabel: boolean;
  onNavigate: () => void;
}

function navTestId(to: string): string {
  const slug = to.replace(/^\//, '').replace(/\//g, '-') || 'home';
  return `admin.nav.${slug}`;
}

function SidebarLink({ item, showLabel, onNavigate }: SidebarLinkProps) {
  const Icon = item.icon;
  return (
    <NavLink
      id={navTestId(item.to)}
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      title={showLabel ? undefined : item.label}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition',
          showLabel ? 'justify-start' : 'justify-center',
          isActive
            ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-200'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white',
        )
      }
    >
      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      {showLabel ? <span className="truncate">{item.label}</span> : null}
    </NavLink>
  );
}
